// @flow

import * as React from 'react';
import { ErrorBoundary } from '@ta/common.error-handling';
import { SocialSection } from '@ta/social.sections';
import { RouteConsumer } from '@ta/common.routing';
import type { SuggestedFolloweesPlacementInput } from '@ta/social.member-types';
import { FollowPlacementContext } from '@ta/tracking.interactions';

import ExternalAccountTypeDataProvider from './ExternalAccountTypeDataProvider';
import FollowBlock from './FollowBlock';
import RecommendedFolloweeDataProvider from './RecommendedFolloweeDataProvider';
import SuggestedMembersModalCombined from './SuggestedMembersModalCombined';

import styles from './FollowSection.less';

/** A pretty list of followee cards */

type Props = {|
  limit?: number,
  onError?: any => void,
  placement?: SuggestedFolloweesPlacementInput,
  useLoadingSkeleton?: boolean,
|};

const FollowSection = ({ limit, placement, onError, useLoadingSkeleton }: Props) => (
  <SocialSection className={styles.section}>
    <ErrorBoundary>
      <FollowPlacementContext.Provider value={{ placement: 'SUGGESTED_FOLLOWEES_RIGHT_RAIL' }}>
        <RecommendedFolloweeDataProvider
          limit={limit}
          placement={placement}
          onError={onError}
          useLoadingSkeleton={useLoadingSkeleton}
        >
          {suggestedFollowees => <FollowBlock suggestedFollowees={suggestedFollowees} />}
        </RecommendedFolloweeDataProvider>
      </FollowPlacementContext.Provider>
      <FollowPlacementContext.Provider value={{ placement: 'SUGGESTED_FOLLOWEES_MODAL' }}>
        <ExternalAccountTypeDataProvider>
          {profile => (
            <React.Fragment>
              <RouteConsumer>
                {({ route }) => {
                  const { page, username } = route;
                  // For reasons unknown, we need to ensure the usernames for modals.
                  // On /Home, it should be your your username.
                  // On /Profile, it should be the current profile's username.
                  // TODO: Fix this up later as either this logic shouldn't live here or exist in the first place, but are the smallest changes necessary to work
                  return (
                    <SuggestedMembersModalCombined
                      username={page === 'Profile' ? username : profile.username}
                      placement="SHOW_MORE_FROM_RIGHT_RAIL"
                    />
                  );
                }}
              </RouteConsumer>
            </React.Fragment>
          )}
        </ExternalAccountTypeDataProvider>
      </FollowPlacementContext.Provider>
    </ErrorBoundary>
  </SocialSection>
);

FollowSection.defaultProps = {
  limit: 3,
  placement: undefined,
  onError: undefined,
  useLoadingSkeleton: true,
};

export default FollowSection;
